import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ScrollToTop from "react-scroll-to-top";
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Button,
  Container,
  Divider,
  Grid,
  Pagination,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
} from "@mui/material";
import moment from 'moment';
import Loader from "react-js-loader";
import { getUserDetails } from "../../redux/actions/user";
import config from "../../config/config";
import "./style.css";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const AdminDetailPage = () => {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();

  const { loading, clientDetailData } = useSelector(state => ({
    loading: state.user.loading,
    clientDetailData: state.user.clientDetailData,
  }));
  const { bookingDetails = [], perkData = [] } = clientDetailData;

  const [page, setPage] = useState(0);
  const [perkPage, setPerkPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const order = 'asc';
  const orderBy = '';
  const perkRowsPerPage = 5;

  useEffect(() => {
    dispatch(getUserDetails(userId));
  }, []);

  function getDurationText(duration) {
    return duration < 1 ? 'Custom' : duration > 1 ? duration + ' Hours' : duration + ' Hour';
  }

  const handleChangePage = (event, page) => {
    setPage(page - 1);
  }
  const handleChangePerkPage = (event, value) => setPerkPage(value - 1);

  function getDaysRemainingText(expiryDate) {
    const daysRemaining = moment(expiryDate).diff(moment(), 'days') + 1;
    return moment(expiryDate).isBefore(moment())
      ? 'Expired'
      : daysRemaining + ' day' + (daysRemaining > 1 ? 's' : '');
  }

  const onViewPerk = () => {
    navigate(`/${clientDetailData?.username}/perks`);
  }

  return (
    <Container maxWidth="lg">
      <Grid
        item
        className="page-title"
        container
        justifyContent='space-between'
        sx={{ my: { sm: 2, md: 5 }, marginInline: 'auto' }}
      >
        <Grid item xs={12} sm={6}>
          <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 600 }}>Account Details</Typography>
        </Grid>
        <Grid item container direction='row' justifyContent='flex-end' alignItems="center" xs={12} sm={6}>
          <Button color="error" variant="contained" sx={{ marginInlineStart: 3, fontSize: '0.88rem', fontWeight: 500, padding: '10px 30px', textTransform: 'none' }} onClick={() => navigate('/superadmin/edit/' + userId)}>Edit Member Account</Button>
        </Grid>
      </Grid>
      <Grid
        item
        container
        spacing={4}
      >
        <Grid item xs="auto">
          <Container
            direction='column'
            sx={{
              backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d',
              width: '350px',
              minHeight: '300px',
              borderRadius: 2,
              paddingY: 3,
            }}
          >
            {loading ? (
              <Grid marginTop="150px">
                <Loader type="bubble-loop" bgColor={"#2196f3"} color={'#2196f3'} size={100} />
              </Grid>
            ) : (
              <>
                <Grid textAlign="center" sx={{ mt: 2 }}>
                  {clientDetailData?.avatar?.includes("https://gravatar.com/") ?
                    <Avatar
                      sx={{
                        height: '150px',
                        width: '150px',
                        color: '#fff',
                        backgroundColor: 'tomato',
                        fontSize: '75px',
                        fontWeight: 600,
                        margin: 'auto',
                      }}
                    >
                      {clientDetailData?.firstName ? clientDetailData?.firstName[0] : ''}
                    </Avatar> :
                    <img src={`${config.SERVER_BASE_URL}/${clientDetailData.avatar}`} style={{ width: '150px', height: '150px', borderRadius: '6px' }} alt="avatar" />
                  }
                </Grid>
                <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, textAlign: 'center', mt: 3 }}>{clientDetailData?.firstName + ' ' + clientDetailData?.lastName}</Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, textAlign: 'center', color: theme.palette.mode ==="light" ?  '#a1a5b7' : '#565674' }}>
                  {clientDetailData?.city ? clientDetailData?.city + "," : ""} {clientDetailData?.country}
                </Typography>
                <Grid sx={{ textAlign: 'center', mt: 2 }}>
                  {clientDetailData?.membership === 0
                    ? <Box className="booking-detail-type" sx={{ color: '#FFC700', backgroundColor: theme.palette.mode ==="light" ? "#FFF8DD" : "#392F28", fontWeight: 'bold' }}>Trial User</Box>
                    : clientDetailData?.membership === 1
                      ? <Box className="booking-detail-type" sx={{ color: '#F1416C', backgroundColor: theme.palette.mode ==="light" ? "#FFF5F8" : "#3A2434", fontWeight: 'bold' }}>Expired User</Box>
                      : <Box className="booking-detail-type" sx={{ color: '#50CD89', backgroundColor: theme.palette.mode === 'light' ? "#E8FFF3" : "#1C3238", fontWeight: 'bold' }}>Pro User</Box>
                  }
                </Grid>
                <Divider sx={{ mt: 2 }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600, mt: 3, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#3f4254' : '#fff' }}>Account ID</Typography>
                <Typography sx={{ fontSize: '14px', mt: 0.5, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#7E8299' : '#6D6D80' }}>{clientDetailData?.bookFormId}</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, mt: 3, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#3f4254' : '#fff' }}>Email</Typography>
                <Typography sx={{ fontSize: '14px', mt: 0.5, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#7E8299' : '#6D6D80' }}>{clientDetailData?.email}</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, mt: 3, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#3f4254' : '#fff' }}>Website</Typography>
                <Typography sx={{ fontSize: '14px', mt: 0.5, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#7E8299' : '#6D6D80' }}>{clientDetailData?.website?.replace(/https?:\/\//i, "") || "N/A"}</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, mt: 3, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#3f4254' : '#fff' }}>Member Since</Typography>
                <Typography sx={{ fontSize: '14px', mt: 0.5, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#7E8299' : '#6D6D80' }}>{moment(clientDetailData?.createdAt).format('D MMMM YYYY')}</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, mt: 3, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#3f4254' : '#fff' }}>Membership Expiry</Typography>
                <Typography sx={{ fontSize: '14px', mt: 0.5, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#7E8299' : '#6D6D80' }}>{moment(clientDetailData?.expireDate).format('D MMMM YYYY')}</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, mt: 3, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#3f4254' : '#fff' }}>Last Login</Typography>
                <Typography sx={{ fontSize: '14px', mt: 0.5, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#7E8299' : '#6D6D80', mb: 5 }}>{moment(clientDetailData.lastLogin).fromNow()}</Typography>
              </>
            )}
          </Container>
        </Grid>
        <Grid
          item
          xs
          sx={{
            minHeight: '300px',
            borderRadius: 2
          }}
        >
          <Container
            direction='column'
            sx={{ padding: '48px !important', backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d', minHeight: '300px', borderRadius: 2 }}
          >
            {loading ? (
              <Grid marginTop="150px">
                <Loader type="bubble-loop" bgColor={"#2196f3"} color={'#2196f3'} size={100} />
              </Grid>
            ) : (
              <>
                <Grid>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.5rem' }}>All Bookings</Typography>
                  <Typography sx={{ fontSize: '0.8rem', mt: 0.5 }}>This is a list of all entries made through this member's booking form.</Typography>
                </Grid>
                <Grid sx={{ mt: 3 }}>
                  <TableContainer sx={{ width: '100%' }}>
                    <Table aria-label="simple table">
                      <TableBody>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>NAME</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>OCCUPATION</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>ENQUIRY DATE</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>DURATION</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>STATUS</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>ACTIONS</TableCell>
                        </TableRow>
                        {bookingDetails.length > 0 && stableSort(bookingDetails, getComparator(order, orderBy))
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((row, index) => {
                            return (
                              <TableRow
                                tabIndex={-1}
                                key={index}
                                sx={{ padding: 0, borderBottom: "1px dashed #eff2f5", '&:last-child td, &:last-child th': { border: 0 } }}
                              >
                                <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>{row?.client?.firstName + ' ' + row?.client?.lastName}</TableCell>
                                <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>{row?.client.occupation}</TableCell>
                                <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>{moment(row?.preferredDate).format('D MMM YYYY')}</TableCell>
                                <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>{getDurationText(row.duration)}</TableCell>
                                <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>
                                  {
                                    row.status === 0
                                      ? <Box className="booking-detail-type" sx={{ color: '#FFC700', backgroundColor: theme.palette.mode ==="light" ? "#FFF8DD" : "#392F28" }}>Pending</Box>
                                      : (row.status === 1
                                        ? <Box className="booking-detail-type" sx={{ color: '#F1416C', backgroundColor: theme.palette.mode ==="light" ? "#FFF5F8" : "#3A2434" }}>Declined</Box>
                                        : <Box className="booking-detail-type" sx={{ color: '#50CD89', backgroundColor: theme.palette.mode === 'light' ? "#E8FFF3" : "#1C3238" }}>Approved</Box>)
                                  }
                                </TableCell>
                                <TableCell sx={{ paddingX: 0, borderBottomStyle: "none" }} align="left">
                                  <Button
                                    onClick={() => navigate('/bookings/details/' + row._id)}
                                    sx={{
                                      minWidth: '53px',
                                      backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#2b2b40',
                                      color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80',
                                      padding: '11px 13px',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      lineHeight: '16px',
                                      fontWeight: 500,
                                      textTransform: 'none',
                                    }}
                                  >
                                    View
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Grid sx={{ mt: 1 }} container justifyContent='space-between' alignItems="center">
                    <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
                      Displaying {page * rowsPerPage + 1} - {Math.min((page + 1) * rowsPerPage, bookingDetails.length)} of {bookingDetails.length} entries
                    </Typography>
                    <Pagination
                      count={Math.ceil(bookingDetails.length / rowsPerPage)}
                      shape="rounded"
                      color="primary"
                      onChange={handleChangePage}
                      sx={{
                        "& .MuiPaginationItem-root": {
                          '&.Mui-selected': {
                            color: '#fff'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Container>
          <Container
            direction='column'
            sx={{ mt: 3, padding: '48px !important', backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d', minHeight: '300px', borderRadius: 2 }}
          >
            {loading ? (
              <Grid marginTop="150px">
                <Loader type="bubble-loop" bgColor={"#2196f3"} color={'#2196f3'} size={100} />
              </Grid>
            ) : (
              <>
                <Grid>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.5rem' }}>All Perks</Typography>
                  <Typography sx={{ fontSize: '0.8rem', mt: 0.5 }}>This is a list of all Perks created by this member.</Typography>
                </Grid>
                <Grid sx={{ mt: 3 }}>
                  <TableContainer sx={{ width: '100%' }}>
                    <Table aria-label="simple table">
                      <TableBody>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>TITLE</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>VIEWS</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>DAYS LEFT</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>STATUS</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>ACTIONS</TableCell>
                        </TableRow>
                        {perkData.length > 0 && stableSort(perkData, getComparator(order, orderBy))
                          .slice(perkPage * perkRowsPerPage, perkPage * perkRowsPerPage + perkRowsPerPage)
                          .map((row, index) => {
                            return (
                              <TableRow
                                tabIndex={-1}
                                key={index}
                                sx={{ padding: 0, borderBottom: "1px dashed #eff2f5", '&:last-child td, &:last-child th': { border: 0 } }}
                              >
                                <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>{row?.title}</TableCell>
                                <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>{row?.visitCount}</TableCell>
                                <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>{getDaysRemainingText(row.expiryDate)}</TableCell>
                                <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', borderBottomStyle: "none" }}>
                                  {row.status === 0 ?
                                    <Box className="booking-detail-type" sx={{ color: '#50CD89', backgroundColor: theme.palette.mode ==="light" ? "#E8FFF3" : "#1C3238" }}>Active</Box> :
                                    row.status === 1 ?
                                      <Box className="booking-detail-type" sx={{ color: '#FFC700', backgroundColor: theme.palette.mode ==="light" ? "#FFF8DD" : "#392F28" }}>Inactive</Box> :
                                      <Box className="booking-detail-type" sx={{ color: '#F1416C', backgroundColor: theme.palette.mode === 'light' ? "#FFF5F8" : "#3A2434" }}>Expired</Box>
                                  }
                                </TableCell>
                                <TableCell sx={{ paddingX: 0, borderBottomStyle: "none" }} align="left">
                                  <Button
                                    onClick={onViewPerk}
                                    sx={{
                                      minWidth: '53px',
                                      backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#2b2b40',
                                      color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80',
                                      padding: '11px 13px',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      lineHeight: '16px',
                                      fontWeight: 500,
                                      textTransform: 'none',
                                    }}
                                  >
                                    View
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Grid sx={{ mt: 1 }} container justifyContent='space-between' alignItems="center">
                    <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
                      Displaying {perkPage * perkRowsPerPage + 1} - {Math.min((perkPage + 1) * perkRowsPerPage, perkData.length)} of {perkData.length} entries
                    </Typography>
                    <Pagination
                      count={Math.ceil(perkData.length / perkRowsPerPage)}
                      shape="rounded"
                      color="primary"
                      onChange={handleChangePerkPage}
                      sx={{
                        "& .MuiPaginationItem-root": {
                          '&.Mui-selected': {
                            color: '#fff'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Container>
        </Grid>
      </Grid>
      <ScrollToTop
        smooth
        color="#009EF7"
      // component={<Button startIcon={<NorthIcon sx={{ color: '#fff' }} />} sx={{ padding: 0 }} variant="contained" />} style={{ padding: 0 }}
      />
    </Container>
  );
}

export default AdminDetailPage;